/**
 * Calendar dates in this project are date-only strings "YYYY-MM-DD".
 * They represent a human calendar day, not an instant in time.
 *
 * - Display must be invariant across timezones: "2023-01-01" is "Jan 1" everywhere.
 * - Filtering is based on the user’s local day and must be DST-safe (no +24h additions).
 * - Do not use Date.parse/new Date on calendar date strings except through the dedicated helpers.
 */

export const CALENDAR_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function isLegacyDateYear(year: number): boolean {
  return year >= 0 && year < 100;
}

function setLocalFullYearSafe(
  date: Date,
  year: number,
  monthArg: number,
  dayArg: number,
): void {
  if (isLegacyDateYear(year)) {
    // monthArg/dayArg are the same arguments passed into Date math at the call site.
    date.setFullYear(year, monthArg, dayArg);
  }
}

function setUtcFullYearSafe(
  date: Date,
  year: number,
  monthArg: number,
  dayArg: number,
): void {
  if (isLegacyDateYear(year)) {
    // monthArg/dayArg are the same arguments passed into Date math at the call site.
    date.setUTCFullYear(year, monthArg, dayArg);
  }
}

/**
 * Parses a date string "YYYY-MM-DD" into its components.
 * Accepts "YYYY-MM-DD" strictly.
 * Throws if the format is invalid or if the date components form an invalid calendar date (e.g. 2023-02-30).
 */
export function parseYmd(dateStr: string): {
  year: number;
  month: number;
  day: number;
} {
  // Strict format check: YYYY-MM-DD
  if (!CALENDAR_DATE_REGEX.test(dateStr)) {
    throw new Error(`Invalid date format: ${dateStr}. Expected YYYY-MM-DD.`);
  }

  const parts = dateStr.split("-");
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  // Validate ranges
  if (month < 1 || month > 12) {
    throw new Error(`Invalid month: ${month} in ${dateStr}`);
  }

  // Days in month validation
  // Date(year, month, 0).getDate() gives the last day of the *previous* month (month is 0-indexed in Date ctor).
  // Here month is 1-based. So new Date(year, month, 0) gives last day of month.
  const d = new Date(year, month, 0);
  // Date constructor maps years 0-99 to 1900-1999. We must use safe full-year setting for validation.
  setLocalFullYearSafe(d, year, month, 0);
  const daysInMonth = d.getDate();
  if (day < 1 || day > daysInMonth) {
    throw new Error(`Invalid day: ${day} for month ${month} in ${dateStr}`);
  }

  return { year, month, day };
}

/**
 * Returns a stable UTC timestamp for the start of the given calendar date.
 * Use this for display, sorting, and grouping to ensure invariance across timezones.
 * effectively: UTC Midnight
 */
export function getUtcMidnightTimestamp(dateStr: string): number {
  const { year, month, day } = parseYmd(dateStr);
  const d = new Date(Date.UTC(year, month - 1, day));
  setUtcFullYearSafe(d, year, month - 1, day);
  return d.getTime();
}

/**
 * Returns the local timestamp for the END of the given calendar date.
 * This is the midnight boundary of the NEXT day in the local timezone.
 * Use this for "upcoming", "past", "today" filtering.
 * DST-safe (does not use fixed +24h addition).
 */
export function getLocalEndOfDayCutoffTimestamp(dateStr: string): number {
  const { year, month, day } = parseYmd(dateStr);
  // Midnight of the NEXT day
  const d = new Date(year, month - 1, day + 1, 0, 0, 0, 0);
  // Set full date components together to avoid rollover drift around leap/non-leap boundaries.
  setLocalFullYearSafe(d, year, month - 1, day + 1);
  return d.getTime();
}

/**
 * Returns the UTC timestamp for the END of the given calendar date.
 * This is the midnight boundary of the NEXT day in UTC.
 * Use this for timezone-independent filtering (e.g. during SSR/SSG).
 */
export function getUtcEndOfDayCutoffTimestamp(dateStr: string): number {
  const { year, month, day } = parseYmd(dateStr);
  const d = new Date(Date.UTC(year, month - 1, day + 1));
  setUtcFullYearSafe(d, year, month - 1, day + 1);
  return d.getTime();
}

export function isEventUpcoming(
  startDate: string,
  endDate: string | null,
  now: number = Date.now(),
): boolean {
  const finalDate = endDate ?? startDate;
  const cutoff = getLocalEndOfDayCutoffTimestamp(finalDate);
  return cutoff > now;
}

export function isEventPast(
  startDate: string,
  endDate: string | null,
  now: number = Date.now(),
): boolean {
  return !isEventUpcoming(startDate, endDate, now);
}

export function isEventUpcomingUtc(
  startDate: string,
  endDate: string | null,
  now: number = Date.now(),
): boolean {
  const finalDate = endDate ?? startDate;
  const cutoff = getUtcEndOfDayCutoffTimestamp(finalDate);
  return cutoff > now;
}
